import { XStack, YStack } from 'tamagui';

import PSSkeleton from '@/components/ui/PSSkeleton';

/**
 * TicketListItemSkeleton — the loading placeholder for the profile page's ticket
 * list (shown while `useFetchBookings` is in flight; see `app/profile`).
 *
 * Mirrors `TicketListItem`'s anatomy — an image bone on the left, title +
 * date-time + ticket-count bones stacked on the right, then a divider and a
 * location bone below — so the real rows slot in without a jump. The image
 * bone uses a fixed height here (the real image's height is driven by its
 * text-column sibling, which a skeleton has no live layout to measure).
 *
 * One deliberate difference from `TicketListItem`: no recessed
 * `$backgroundSecondary` box around the row. The bone color IS
 * `$backgroundSecondary` (see PSSkeleton), so bones inside that box would be
 * invisible — same reason `EventDetailSkeleton` lays its bones straight on the
 * page background. The row's SHAPE still reads; only the frame is dropped while
 * loading.
 *
 * `count` is how many placeholder rows to show (defaults to 3) — enough to fill
 * the list area so it reads as "bookings, loading" rather than a single stray
 * bone. Rendered inside ONE `<PSSkeleton>` so every bone across every row pulses
 * off the same clock.
 */

// Match TicketListItem's image width; height is a stand-in for the real row's
// dynamic (text-column-driven) height.
const IMAGE_W = 72;
const IMAGE_H = 96;

export default function TicketListItemSkeleton({
  count = 3,
}: {
  count?: number;
}) {
  return (
    <PSSkeleton>
      <YStack alignSelf="stretch" gap="$lg">
        {Array.from({ length: count }, (_, i) => (
          <YStack key={i} gap="$md">
            <XStack gap="$md" items="stretch">
              {/* Image bone — stands in for the real row's photo. */}
              <PSSkeleton.Bone style={{ width: IMAGE_W, height: IMAGE_H }} />

              {/* Text column: title, date-time, ticket-count — same stack order
                  as the real row. */}
              <YStack flex={1} gap="$xs">
                {/* Title — up to three lines in the real row; two bones stand in. */}
                <PSSkeleton.Bone style={{ height: 16, width: '90%' }} />
                <PSSkeleton.Bone style={{ height: 16, width: '60%' }} />
                <PSSkeleton.Bone style={{ height: 14, width: '50%' }} />
                <PSSkeleton.Bone style={{ height: 14, width: '40%' }} />
              </YStack>
            </XStack>

            {/* Divider + location bones, matching the real row's bottom row. */}
            <PSSkeleton.Bone style={{ height: 14, width: '70%' }} />
          </YStack>
        ))}
      </YStack>
    </PSSkeleton>
  );
}
